import { useState, useEffect, useRef } from 'react';
import { NextSeo } from 'next-seo';
import { ApiRoutes, AppRoutes } from 'src/lib/routes';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import Slider from 'react-slick';
import { commonCarouselSettings, HEADER_HEIGHT } from 'src/lib/constants';
import HeroImage from 'public/images/gift_card/hero.jpg';
import CorpImage from 'public/images/gift_card/corp.jpg';
import Illu10Image from 'public/images/illu/illu_10.png';
import Illu11Image from 'public/images/illu/illu_11.png';
import Illu9Image from 'public/images/illu/illu_9.png';
import Banner1Image from 'public/images/gift_card/gift_1.jpg';
import Banner2Image from 'public/images/gift_card/gift_2.jpg';
import Banner3Image from 'public/images/gift_card/gift_3.jpg';
import Icon1 from 'public/images/gift_card/icon_1.png';
import Icon2 from 'public/images/gift_card/icon_2.png';
import Icon3 from 'public/images/gift_card/icon_3.png';
import styles from 'src/styles/modules/pages/brand.module.scss';
import videoCardStyle from 'src/styles/modules/components/video_card.module.scss';
import ImageWithFallback from 'src/components/ImageWithFallback';
import Link from 'next/link';
import VideoCard from 'src/components/VideoCard';
import useInitialData from 'src/hooks/useInitialData';
import CorporateInquiry from 'src/components/CorporateInquiry';
import useQuery from 'src/hooks/useQuery';
import { Collection, Pagination, Option, Translation } from 'src/types';
import BrandImageGridV2 from 'src/components/BrandImageGridV2';
import { Overlay, Popover } from 'react-bootstrap';
import { FiChevronDown } from 'react-icons/fi';
import { sortBy } from 'lodash-es';
import classNames from 'classnames';
import { scrollTo, getEleOffset } from 'src/helpers';
import Player from 'src/lib/video_player';
import noVideoImg from 'public/images/gift_card/no_video.png';
import { useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import { GetStaticProps } from 'next';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';

const messages = [
  {
    titleTranslationKey: 'messages.msg1.title',
    subTranslationKey: 'messages.msg1.subTitle',
    image: Banner1Image,
    altTranslationKey: 'messages.msg1.alt',
    url: 'https://www.seeksophie.com/collections/singapore-best-fishing-in-singapore-cpo509m5w4',
  },
  {
    titleTranslationKey: 'messages.msg2.title',
    subTranslationKey: 'messages.msg2.subTitle',
    altTranslationKey: 'messages.msg2.alt',
    image: Banner2Image,
    url: 'https://www.seeksophie.com/collections/singapore-gift-experiences-c9g5800jn6/gifts-for-foodie-friends',
  },
  {
    titleTranslationKey: 'messages.msg3.title',
    subTranslationKey: 'messages.msg3.subTitle',
    altTranslationKey: 'messages.msg3.alt',
    image: Banner3Image,
    url: 'https://www.seeksophie.com/collections/singapore-gift-experiences-c9g5800jn6/creative-gift-experiences',
  },
];

const GiftCollection: React.FC = () => {
  const translation = useTranslations('pages/gifts/index')
  const selectorRef = useRef<HTMLDivElement>(null);
  const initialData = useInitialData();
  const [selectedCountryId, setSelectedCountryId] = useState<number>();
  const [show, setShow] = useState(false);

  const { data } = useQuery<{ data: Collection[]; pagination: Pagination }>(
    ApiRoutes.apiCollectionsRoute(
      {},
      {
        view: 'full',
        q: { gift_country_id_in: initialData.countries.map((o) => o.id).filter((v) => v) },
      }
    ).toUrl()
  );

  const { data: collections = [] } = data || {};
  const selectedCollection =
    collections.length > 0 && selectedCountryId
      ? collections.find((o) => o.giftCountryId === selectedCountryId)
      : undefined;

  const { data: featuredOptionsData } = useQuery<{ data: Option[]; pagination: Pagination }>(
    selectedCollection?.id
      ? ApiRoutes.apiCollectionFeaturedGiftOptionsRoute(
          { id: selectedCollection.id },
          {
            per_page: 5,
          }
        ).toUrl()
      : null
  );

  const { data: featuredOptions = [] } = featuredOptionsData || {};


  useEffect(() => {
    if (!selectedCountryId) {
      const singapore = initialData.countries.find((o) => o.slug === 'singapore');
      setSelectedCountryId(singapore?.id);
    }
  }, [initialData.countries.length]);

  return (
    <>
      <div
        ref={selectorRef}
        className={
          'tw-bg-white tw-mx-auto tw-w-full md:tw-w-80  tw-flex tw-justify-between tw-items-center tw-leading-none md:tw-py-3 tw-py-2 tw-px-6 md:tw-px-8 tw-rounded-4xl tw-border tw-border-brand-navy tw-cursor-pointer tw-mb-8 md:tw-mb-16'
        }
        onClick={() => setShow(true)}
      >
        <div>
          {selectedCountryId ? (
            <span className="tw-font-semibold">
              {initialData.countries.find((o) => o.id === selectedCountryId)?.name}
            </span>
          ) : (
            <span className="tw-text-grey-light">Select Country...</span>
          )}
        </div>
        <FiChevronDown size={20} />
      </div>
      <Overlay
        rootClose={true}
        target={selectorRef}
        show={show}
        placement="bottom"
        onHide={() => {
          setShow(false);
        }}
        popperConfig={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
          ],
        }}
      >
        <Popover id="country-selector" className={`tw-w-72 tw-py-3 tw-px-0 popover-no-arrow`}>
          <div className="md:tw-max-h-sm tw-overflow-y-auto tw-relative">
            <Popover.Content className="tw-px-3">
              {sortBy(initialData.countries, [(o) => -o.optionsCount]).map((country) => (
                <div
                  key={country.id}
                  onClick={() => {
                    setSelectedCountryId(country.id)
                    setShow(false)
                  }}
                  className={classNames('tw-py-1 tw-cursor-pointer tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none tw-font-semibold tw-font-styleSans hover:tw-text-brand-navy tw-text-center', {
                    'tw-text-brand-navy': selectedCountryId === country.id,
                    'tw-text-grey': selectedCountryId !== country.id,
                  })}
                >
                  {country.name}
                </div>
              ))}
            </Popover.Content>
          </div>
        </Popover>
      </Overlay>
      <BrandImageGridV2 key={selectedCountryId} options={featuredOptions.slice(0, 5)} isGift={true} />
      <div className="tw-mt-8 md:tw-mt-16 tw-text-center">
        <Link legacyBehavior prefetch={false} href={selectedCollection ? AppRoutes.collectionRoute({ id: selectedCollection.slugCode }).toPath() : '#'}>
          <a className="btn btn-primary">{translation('exploreGiftCollection')}</a>
        </Link>
      </div>
    </>
  );
};

const Page = () => {
  const addLocaleToUrl = useAddLocaleToUrl()
  const translation = useTranslations('pages/gifts/index')
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [player, setPlayer] = useState<Player>();
  const containerOptionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const autoPlayVideo = () => {
      const videoContainerEle = videoContainerRef.current;
      const currentPos = window.pageYOffset;
      if (videoContainerEle && player) {
        const loadMorePos = getEleOffset(videoContainerEle).top - HEADER_HEIGHT;
        if (
          currentPos > loadMorePos &&
          currentPos <= loadMorePos + videoContainerEle.offsetHeight
        ) {
          player.play();
        } else {
          player.pause();
        }
      }
    };

    document.addEventListener('scroll', autoPlayVideo);

    return () => {
      document.removeEventListener('scroll', autoPlayVideo);
    };
  }, [player]);

  return (
    <>
      <NextSeo
        title={translation('metaTitle', {year: new Date().getFullYear()})}
        description={translation('metaDescription')}
        canonical={addLocaleToUrl(AppRoutes.giftsRoute().toUrl())}
        openGraph={{
          url: addLocaleToUrl(AppRoutes.giftsRoute().toUrl()),
          title: translation('metaTitle', {year: new Date().getFullYear()}),
          description: translation('metaDescription'),
        }}
      />
      <WithDefaultJsonLd />

      <div className={styles.heroContainer}>
        <ImageWithFallback
          src={HeroImage}
          alt={translation('altHeroImage')}
          className="tw-object-cover tw-w-full tw-h-full"
        />

        <div className="tw-absolute tw-bg-black tw-opacity-20 tw-w-full tw-h-full tw-left-0 tw-bottom-0 tw-rounded-lg"></div>
        <div className="tw-absolute tw-top-10 md:tw-top-40 tw-w-full tw-px-6 md:tw-px-10 tw-z-10">
          <div className="container">
            <h1 className="tw-text-center md:tw-text-left tw-font-styleSans tw-text-6xl md:tw-text-8xl tw-font-semibold tw-text-brand-navy tw-leading-none md:tw-leading-none">
              {translation('giftAMemory')}
              <br />
              {translation('theyWont')} <br />
              {translation('forget')}
            </h1>
          </div>
        </div>
      </div>

      <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24" ref={videoContainerRef}>
        <div className="container">
          <div className="row tw-items-center">
            <div className="col-md-6">
              <div className="md:tw-ml-32">
                <div className="tw-text-center md:tw-text-left">
                  <ImageWithFallback
                    src={Illu10Image}
                    alt=""
                    className="tw-static tw-inline-block tw-w-40 md:tw-w-56 tw-h-auto tw-object-cover tw-mb-6"
                  />
                  <div className="tw-text-brand-navy tw-font-styleSans tw-font-semibold tw-text-4xl tw-leading-none tw-mb-4">
                    {translation('searching')} <br />
                    {translation('surprise')} <br />
                    {translation('ofTheOrdinary')}
                  </div>
                  <div className="tw-hidden md:tw-block">
                    {translation('giftYourFavourite')} <br />
                    {translation('experienceTheyWillLove')}
                  </div>
                </div>
              </div>

              <div className="tw-border-t tw-border-grey-lighter tw-mt-14 tw-block md:tw-hidden"></div>
            </div>

            <div className="col-md-6">
              <div className="tw-flex md:tw-flex-col md:tw-items-end">
                <div className="video tw-hidden md:tw-block">
                  <div
                    className={`${videoCardStyle.videoContainer} ${styles.videoCard} tw-mx-auto tw-border-2 tw-border-brand-green`}
                  >
                    <VideoCard videoId={'pMoTqLYBLgk'} unmuteOnPlay={true} setPlayer={setPlayer} noVideoImg={noVideoImg} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="tw-mt-16 md:tw-mt-20">
            <Slider
              {...commonCarouselSettings}
              arrows={false}
              dots={true}
              slidesToShow={3}
              slidesToScroll={1}
              className="carousel-homework tw--mx-3.75"
              responsive={[
                {
                  breakpoint: 800,
                  settings: {
                    slidesToShow: 1,
                  },
                },
              ]}
            >
              {messages.map((message, index) => (
                <Link legacyBehavior prefetch={false} key={index} href={message.url}>
                  <a className="tw-flex tw-flex-col tw-px-3.75">
                    <ImageWithFallback
                      src={message.image}
                      alt={translation(message.altTranslationKey)}
                      className="tw-block tw-static tw-h-80 tw-w-full tw-object-cover md:tw-mb-4 tw-mb-3 tw-border-2 tw-border-solid tw-border-brand-green tw-rounded-lg tw-order-3 md:tw-order-1"
                    />
                    <div className="tw-text-xs md:tw-text-sm tw-uppercase tw-font-semibold tw-mb-4 tw-text-center tw-text-brand-navy tw-order-1 md:tw-order-2 tw-leading-none md:tw-leading-none">
                      {translation(message.subTranslationKey)}
                    </div>
                    <div className="tw-mb-4 md:tw-mb-0 tw-text-3xl md:tw-text-4xl tw-text-center tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-brand-navy tw-order-2 md:tw-order-3">
                      {translation(message.titleTranslationKey)}
                    </div>
                  </a>
                </Link>
              ))}
            </Slider>
          </div>
        </div>
      </div>

      <div className="tw-bg-brand-green tw-py-16 md:tw-py-24">
        <div className="container">
          <div className="tw-flex tw-justify-center tw-w-full tw-mb-12">
            <h2 className="md:tw-w-auto tw-w-60 tw-text-center tw-font-styleSans tw-text-5xl md:tw-text-6xl tw-font-semibold">{translation('awesomeWaysToGift')}</h2>
          </div>
          <div className="row">
            <div className="col-md-5 tw-text-center">
              <ImageWithFallback
                src={Illu11Image}
                alt=""
                className="tw-w-auto tw-h-44 tw-inline-block tw-static"
              />
              <div className="tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-my-4 md:tw-my-6">
                {translation('giveAnExperience')}
                <br /> {translation('theyWillLove')}
              </div>
              <div>{translation('chooseAnExperience')}</div>
              <div className="tw-mt-4 md:tw-mt-6">
                <button type="button" className="btn btn-primary" onClick={() => {
                  containerOptionsRef.current
                    ? scrollTo(containerOptionsRef.current, -HEADER_HEIGHT)
                    : null
                  }}>↓ {translation('exploreExperiencesByCountry')} ↓</button>
              </div>
            </div>
            <div className="offset-md-2 col-md-5 tw-text-center tw-mt-8 md:tw-mt-0">
              <ImageWithFallback
                src={Illu9Image}
                alt=""
                className="tw-w-auto tw-h-44 tw-inline-block tw-static"
              />
              <div className="tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-my-4 md:tw-my-6">
                {translation('giveTheBestGift')} <br />
                {translation('cardEver')}
              </div>
              <div>{translation('giveThemFreedom')}</div>
              <div className="tw-mt-4 md:tw-mt-6">
                <Link legacyBehavior prefetch={false} href={AppRoutes.giftsSendRoute().toPath()}>
                  <a className="btn btn-primary">{translation('getThemAGiftCard')}</a>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24" ref={containerOptionsRef}>
        <div className="container">
          <h2 className="tw-max-w-xs md:tw-max-w-full tw-mx-auto tw-text-5xl md:tw-text-6xl tw-mb-12 md:tw-mb-16 tw-font-styleSans tw-font-semibold tw-text-brand-navy tw-text-center tw-leading-none md:tw-leading-none">
            {translation('IdLikeToSeeGiftsFrom')}
          </h2>

          <GiftCollection />
        </div>

        <div className="container tw-border-t tw-border-grey-lighter tw-my-10 md:tw-my-16"></div>

        <div className="container">
          <h2 className="tw-max-w-xs md:tw-max-w-full tw-mx-auto tw-text-5xl md:tw-text-6xl tw-mb-12 md:tw-mb-16 tw-font-styleSans tw-font-semibold tw-text-brand-navy tw-text-center tw-leading-none md:tw-leading-none">
            {translation('giftingDoesntGetAny')}
            <br className="tw-hidden md:tw-inline" /> {translation('easierThanThis')}
          </h2>

          <Slider
            slidesToShow={3}
            slidesToScroll={1}
            dots={false}
            arrows={false}
            className="slider-v2 carousel-homework"
            responsive={[
              {
                breakpoint: 800,
                settings: {
                  slidesToShow: 1,
                  dots: true
                },
              },
            ]}
          >
            <div className="tw-px-3.75 tw-text-center tw-mb-8 md:tw-mb-0">
              <div className="tw-py-8 tw-px-8 tw-border tw-border-brand-navy tw-rounded-2xl">
                <ImageWithFallback
                  src={Icon1}
                  alt=""
                  className={`tw-inline-block tw-static tw-h-6 tw-w-6`}
                />
                <div className="tw-font-styleSans tw-font-semibold tw-text-xl md:tw-text-2xl md:tw-leading-none tw-leading-none tw-my-4">
                  {translation('chooseGiftCardOrExperience')}
                </div>
                <div className="tw-h-24">{translation('theyAreGonnaLoveIt')}</div>
              </div>
            </div>
            <div className="tw-px-3.75 tw-text-center tw-mb-8 md:tw-mb-0">
              <div className="tw-py-8 tw-px-8 tw-border tw-border-brand-navy tw-rounded-2xl">
                <ImageWithFallback
                  src={Icon2}
                  alt=""
                  className={`tw-inline-block tw-static tw-h-6 tw-w-6`}
                />
                <div className="tw-font-styleSans tw-font-semibold tw-text-xl md:tw-text-2xl md:tw-leading-none tw-leading-none tw-my-4">
                  {translation('waitImpatiently')}
                </div>
                <div className="tw-h-24">
                  {translation('yourRecipientReceives')}
                </div>
              </div>
            </div>
            <div className="tw-px-3.75 tw-text-center">
              <div className="tw-py-8 tw-px-8 tw-border tw-border-brand-navy tw-rounded-2xl">
                <ImageWithFallback
                  src={Icon3}
                  alt=""
                  className={`tw-inline-block tw-static tw-h-6 tw-w-6`}
                />
                <div className="tw-font-styleSans tw-font-semibold tw-text-xl md:tw-text-2xl md:tw-leading-none tw-leading-none tw-my-4">
                  {translation('enjoyAMaazing')}
                </div>
                <div className="tw-h-24">
                  {translation('theyAreOnTheir')}
                </div>
              </div>
            </div>
          </Slider>
        </div>
      </div>

      <div className="tw-py-16 md:tw-py-24">
        <div className="container">
          <div className="row tw-items-center">
            <div className="col-md-6 tw-mb-8 md:tw-mb-0">
              <ImageWithFallback
                src={CorpImage}
                alt={translation('altCorpImage')}
                className="tw-h-auto md:tw-h-96 tw-w-full tw-static tw-block tw-object-cover tw-border-2 tw-border-brand-green tw-border-solid tw-rounded-lg"
              />
            </div>
            <div className="col-md-6">
              <div className="tw-max-w-xs md:tw-max-w-full tw-mx-auto tw-font-styleSans tw-font-semibold tw-text-2xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-text-center tw-mb-8">
                {translation('lookingForCorporateGifts')}
              </div>
              <div className="tw-mb-8 tw-text-center">
                {translation('weAreYourHype')}
              </div>
              <div className="tw-text-center">
                <CorporateInquiry />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const translations = await prepareTranslations(context, ['pages/gifts/index']);

  return { props: translations }
};

export default Page;
